import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Share2, Star, ChevronDown, Phone, Moon, Sun } from "lucide-react";
import {
  Button,
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem,
} from "@heroui/react";
import { useTranslation } from "react-i18next";
import {
  NavbarSearch,
  RatingModal,
  ShareModal,
  LanguageDropDown,
} from "@/components";
import { useIsStandalone } from "@/hooks";
import { useTheme } from "@/hooks/useTheme";
import type { EntityModel } from "@/types";
import { AnimatePresence, motion } from "framer-motion";

export const Navbar = ({
  logo,
  name,
  data,
}: {
  logo: string;
  name: string;
  data?: EntityModel;
}) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [shareUrl, setShareUrl] = useState("");
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const location = useLocation();
  const isStandalone = useIsStandalone();
  const { isDark, toggleTheme } = useTheme();

  // Use transparent style only on Home page AND on mobile
  const [isMobile, setIsMobile] = useState(typeof window !== "undefined" ? window.innerWidth < 1024 : false);
  
  useEffect(() => {
    if (typeof window === "undefined") return;
    const handleResize = () => setIsMobile(window.innerWidth < 1024);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const isHome = location.pathname === "/" && isMobile;

  useEffect(() => {
    if (typeof window === "undefined") return;
    setShareUrl(window.location.href);
  }, []);

  // Lock body scroll when sidebar open
  useEffect(() => {
    if (sidebarOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [sidebarOpen]);

  const allNavLinks = [
    { href: "/", label: t("NAV.HOME", "الرئيسية") },
    { href: "/full-menu", label: t("NAV.MENU", "القائمة") },
    { href: "/about", label: t("NAV.ABOUT", "من نحن") },
    { href: "/contact", label: t("NAV.CONTACT", "اتصل بنا") },
    { href: "/gallery", label: t("NAV.GALLERY", "المعرض") },
    { href: "/reservations", label: t("NAV.RESERVATIONS", "الحجوزات") },
    { href: "/testimonials", label: t("NAV.TESTIMONIALS", "آراء العملاء") },
    { href: "/careers", label: t("NAV.CAREERS", "الوظائف") },
  ];

  const mainNavLinks = allNavLinks.slice(0, 5);
  const secondaryNavLinks = allNavLinks.slice(5);

  const isActiveLink = (href: string) => location.pathname === href;

  return (
    <>
      <header
        className={`z-50 transition-colors duration-300 ${
          isHome
            ? "absolute top-0 left-0 right-0"
            : "sticky top-0 bg-bodyBg/95 backdrop-blur-md border-b border-border-2/60"
        }`}
      >
        {/* Wider container — nearly full screen */}
        <div className="navbar-container py-2">
          <div
            className={`relative flex items-center justify-between gap-1 min-[375px]:gap-2 px-2 min-[375px]:px-3 py-2 md:gap-3 md:px-4 md:py-2.5 lg:gap-4 lg:px-5 lg:py-3 xl:px-6 rounded-b-2xl lg:rounded-none ${
              isHome ? "bg-transparent pb-6" : "bg-bodyBg"
            }`}
          >
            {/* Start: Mobile Menu OR Desktop Logo */}
            <div className="flex min-w-0 items-center lg:flex-[0.3]">
              <button
                onClick={() => setSidebarOpen(true)}
                className={`shrink-0 md:hidden size-8 min-[375px]:size-9 sm:size-10 rounded-lg border transition-all duration-200 flex items-center justify-center ${
                  isHome
                    ? "bg-white/8 border-white/12 text-[#EAE4DB] hover:bg-white/14"
                    : "bg-primary/8 border-primary/15 text-primary hover:bg-primary/12"
                }`}
                aria-label="Open menu"
              >
                <Menu
                  className={`size-4 min-[375px]:size-5 ${isHome ? "text-[#EAE4DB]" : "text-primary"}`}
                />
              </button>

              <Link
                to="/"
                className="hidden md:flex relative h-10 w-22 min-w-0 shrink-0 rounded-lg px-1.5 sm:h-11 sm:w-24 md:h-12 md:w-28 lg:h-12 lg:w-32 md:px-2 items-center justify-center"
              >
                <img
                  src={isDark ? "/images/logo-new.png" : "/images/logo.png"}
                  alt={name || "logo"}
                  className="object-contain w-full h-full"
                />
              </Link>
            </div>

            {/* Center: Mobile Logo OR Desktop Navigation */}
            <div className="flex flex-1 items-center justify-center">
              <Link
                to="/"
                className="md:hidden relative h-8 w-18 min-[375px]:h-10 min-[375px]:w-22 min-w-0 shrink-0 rounded-lg px-1 min-[375px]:px-1.5 flex items-center justify-center"
              >
                <img
                  src={isDark ? "/images/logo-new.png" : "/images/logo.png"}
                  alt={name || "logo"}
                  className="object-contain w-full h-full"
                />
              </Link>

              {!isStandalone && (
                <nav className="hidden md:flex min-w-0 flex-1 items-center justify-center gap-1 lg:gap-1.5 xl:gap-2 whitespace-nowrap">
                  {mainNavLinks.map((link, i) => (
                    <Link
                      key={link.href}
                      to={link.href}
                      aria-current={isActiveLink(link.href) ? "page" : undefined}
                      className={`px-3 py-2 lg:px-4 lg:py-2.5 rounded-lg text-xs lg:text-sm font-semibold whitespace-nowrap transition-all duration-300 ${
                        i >= 3 ? "hidden xl:flex" : ""
                      } ${
                        isActiveLink(link.href)
                          ? isHome
                            ? "bg-white/18 text-[#EAE4DB] shadow-[0_8px_28px_rgba(0,0,0,0.14)]"
                            : "bg-primary text-white shadow-sm"
                          : isHome
                            ? "text-[#EAE4DB]/90 hover:text-[#EAE4DB] hover:bg-white/12"
                            : "text-primary/85 hover:text-primary hover:bg-primary/8"
                      }`}
                    >
                      {link.label}
                    </Link>
                  ))}

                  <div className="hidden md:block shrink-0">
                    <Dropdown placement="bottom-end">
                      <DropdownTrigger>
                        <Button
                          variant="light"
                          className={`px-3 py-2 lg:px-4 lg:py-2.5 rounded-lg text-xs lg:text-sm font-semibold whitespace-nowrap transition-all duration-300 ${
                            isHome
                              ? "text-[#EAE4DB]/90 hover:text-[#EAE4DB] hover:bg-white/12"
                              : "text-primary/85 hover:text-primary hover:bg-primary/8"
                          }`}
                          endContent={
                            <ChevronDown className="w-3.5 h-3.5 lg:w-4 lg:h-4" />
                          }
                        >
                          {t("NAV.MORE", "المزيد")}
                        </Button>
                      </DropdownTrigger>
                      <DropdownMenu
                        aria-label="More navigation"
                        className="bg-background/95 backdrop-blur-xl border border-primary/10 shadow-xl"
                      >
                        {secondaryNavLinks.map((link) => (
                          <DropdownItem
                            key={link.href}
                            className={`transition-colors ${
                              isActiveLink(link.href)
                                ? "bg-primary/10 text-primary"
                                : "text-foreground hover:bg-primary/5"
                            }`}
                            onPress={() => (window.location.href = link.href)}
                          >
                            {link.label}
                          </DropdownItem>
                        ))}
                      </DropdownMenu>
                    </Dropdown>
                  </div>
                </nav>
              )}
            </div>

            {/* Right: Actions */}
            <div className="flex shrink-0 items-center justify-end gap-1 min-[375px]:gap-1.5 md:gap-2 lg:gap-2.5 xl:gap-3 lg:flex-[0.3]">
              {data?.phone && (
                <a
                  href={`tel:${data.phone}`}
                  className={`hidden xl:flex items-center gap-2 rounded-lg px-4 py-2 border transition-all duration-300 text-xs lg:text-sm font-medium ${
                    isHome
                      ? "text-[#EAE4DB] bg-white/8 border-white/12 hover:bg-white/14"
                      : "text-primary bg-white border-primary/15 hover:bg-primary/5"
                  }`}
                  dir="ltr"
                >
                  <Phone className="size-4 lg:size-5" />
                  <span>{data.phone}</span>
                </a>
              )}

              <NavbarSearch
                triggerClassName={
                  isHome
                    ? "border-white/12 bg-white/8 text-[#EAE4DB] shadow-none transition-all duration-200 hover:bg-white/14 !size-8 min-[375px]:!size-9 lg:!size-10"
                    : "border-primary/15 bg-white text-primary shadow-none transition-all duration-200 hover:bg-primary/5 !size-8 min-[375px]:!size-9 lg:!size-10"
                }
              />

              <LanguageDropDown variant="minimal" isTransparent={isHome} />

              {/* Theme Toggle — hidden on very small screens (<340px), available in sidebar */}
              <button
                aria-label={isDark ? "Switch to Light Mode" : "Switch to Dark Mode"}
                className={`hidden min-[340px]:flex size-8 min-[375px]:size-9 lg:size-10 rounded-lg border hover:shadow-sm transition-all duration-200 items-center justify-center ${
                  isHome
                    ? "bg-white/8 border-white/12 text-[#EAE4DB] hover:bg-white/14"
                    : "bg-white dark:bg-black/20 border-primary/15 dark:border-white/10 text-primary dark:text-white/80 hover:bg-primary/5 dark:hover:bg-white/10"
                }`}
                onClick={toggleTheme}
              >
                {isDark ? <Sun className="size-4" /> : <Moon className="size-4" />}
              </button>

              {/* Share — hidden on tiny screens (<375px), available in sidebar */}
              <ShareModal
                url={shareUrl}
                renderTrigger={(open) => (
                  <button
                    aria-label="Share"
                    className={`hidden min-[375px]:flex size-8 min-[375px]:size-9 lg:size-10 rounded-lg border hover:shadow-sm transition-all duration-200 items-center justify-center ${
                      isHome
                        ? "bg-white/8 border-white/12 text-[#EAE4DB] hover:bg-white/14"
                        : "bg-white border-primary/15 text-primary hover:bg-primary/5"
                    }`}
                    onClick={open}
                  >
                    <Share2 className="size-4" />
                  </button>
                )}
              />

              <RatingModal
                renderTrigger={(open) => (
                  <>
                    <button
                      aria-label={t("KEY.RATE_RESTAURANT", "قيّم المطعم")}
                      className={`hidden 2xl:inline-flex items-center gap-2 rounded-lg px-4 py-2 text-xs lg:text-sm font-bold whitespace-nowrap hover:shadow-sm transition-all duration-200 ${
                        isHome
                          ? "bg-white/8 border-white/12 text-[#EAE4DB] hover:bg-white/14"
                          : "bg-white border-primary/15 text-primary hover:bg-primary/5"
                      }`}
                      onClick={open}
                    >
                      <Star className="size-4 fill-current" />
                      <span>{t("KEY.RATE_RESTAURANT", "تقييم")}</span>
                    </button>
                    <button
                      aria-label={t("KEY.RATE_RESTAURANT", "قيّم المطعم")}
                      className={`hidden min-[375px]:flex 2xl:hidden size-8 min-[375px]:size-9 lg:size-10 rounded-lg border hover:shadow-sm transition-all duration-200 items-center justify-center ${
                        isHome
                          ? "bg-white/8 border-white/12 text-[#EAE4DB] hover:bg-white/14"
                          : "bg-white border-primary/15 text-primary hover:bg-primary/5"
                      }`}
                      onClick={open}
                    >
                      <Star className="size-4 fill-current" />
                    </button>
                  </>
                )}
              />
            </div>
          </div>
        </div>
      </header>

      {/* ── Mobile Sidebar ── */}
      <AnimatePresence>
        {sidebarOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              className="fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setSidebarOpen(false)}
            />

            {/* Panel */}
            <motion.aside
              className="fixed top-0 bottom-0 start-0 z-[70] w-72 bg-mainBg shadow-2xl flex flex-col sidebar font-['PlutoRegular']"
              initial={{ x: locale === "en" ? "-100%" : "100%" }}
              animate={{ x: 0 }}
              exit={{ x: locale === "en" ? "-100%" : "100%" }}
              transition={{ type: "spring", damping: 28, stiffness: 300 }}
            >
              {/* Sidebar Header */}
              <div className="flex items-center justify-between p-4 border-b border-primary/10">
                <Link to="/" onClick={() => setSidebarOpen(false)}>
                  <img
                    src={isDark ? "/images/logo-new.png" : "/images/logo.png"}
                    alt={name || "logo"}
                    className="h-24 w-auto object-contain"
                  />
                </Link>
                <button
                  onClick={() => setSidebarOpen(false)}
                  className="p-2 rounded-full hover:bg-primary/10 transition-colors"
                  aria-label="Close menu"
                >
                  <X className="size-5 text-primary" />
                </button>
              </div>

              {/* Nav Links — hidden in standalone/APK mode */}
              {!isStandalone && (
                <nav className="flex-1 overflow-y-auto p-4 space-y-1">
                  {allNavLinks.map((link) => (
                    <Link
                      key={link.href}
                      to={link.href}
                      aria-current={isActiveLink(link.href) ? "page" : undefined}
                      onClick={() => setSidebarOpen(false)}
                      className={`block px-4 py-3 rounded-xl text-sm font-medium transition-colors ${
                        isActiveLink(link.href)
                          ? "bg-primary/10 text-primary font-bold"
                          : "text-foreground hover:bg-primary/5"
                      }`}
                    >
                      {link.label}
                    </Link>
                  ))}
                </nav>
              )}

              {/* Sidebar Footer */}
              <div className="p-4 border-t border-primary/10 space-y-3">
                <LanguageDropDown
                  variant="sidebar"
                  onClose={() => setSidebarOpen(false)}
                />
                
                {/* Theme Toggle (Sidebar) */}
                <button
                  onClick={toggleTheme}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-xl bg-primary/5 hover:bg-primary/10 text-primary transition-colors text-sm font-medium"
                >
                  {isDark ? (
                    <>
                      <Sun className="size-4" />
                      <span>{t("THEME.LIGHT", "الوضع الفاتح")}</span>
                    </>
                  ) : (
                    <>
                      <Moon className="size-4" />
                      <span>{t("THEME.DARK", "الوضع الداكن")}</span>
                    </>
                  )}
                </button>
                {data?.phone && (
                  <a
                    href={`tel:${data.phone}`}
                    className="flex items-center gap-2 px-4 py-3 rounded-xl bg-primary/10 text-primary text-sm font-medium"
                    dir="ltr"
                  >
                    <Phone className="size-4" />
                    <span>{data.phone}</span>
                  </a>
                )}
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </>
  );
};
